
import React from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  CertificationCriteria, 
  StudentCertificationProgress 
} from '@/types/certification';

interface CertificationProgressProps {
  progress?: StudentCertificationProgress | null;
  criteria?: CertificationCriteria | null;
}

const CertificationProgress = ({ progress, criteria }: CertificationProgressProps) => {
  if (!progress || !criteria) {
    return (
      <div className="text-center py-6">
        <p className="text-muted-foreground">No certification progress data available.</p>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Requirements Progress</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {criteria.requirements.map((requirement) => {
            const requirementProgress = progress.requirementProgress.find(
              (rp) => rp.requirementId === requirement.id
            );
            
            const achieved = requirementProgress?.achieved || 0;
            const percentage = (achieved / requirement.maxValue) * 100;
            const isCompleted = requirementProgress?.completed || false;
            
            return (
              <div key={requirement.id} className="space-y-2">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-medium">{requirement.description}</h4>
                    <p className="text-sm text-muted-foreground">
                      {requirement.type} (Weight: {requirement.weight * 100}%)
                    </p>
                  </div>
                  <Badge variant={isCompleted ? "default" : "outline"}>
                    {isCompleted ? "Completed" : `Minimum: ${requirement.minimumValue}%`}
                  </Badge>
                </div>
                
                <div className="w-full flex items-center gap-2">
                  <div className="w-full bg-muted rounded-full h-2.5">
                    <div 
                      className={`h-2.5 rounded-full ${isCompleted ? 'bg-green-500' : 'bg-primary'}`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm whitespace-nowrap">
                    {achieved}/{requirement.maxValue} ({Math.round(percentage)}%)
                  </span>
                </div>
                
                {requirementProgress?.verifiedBy && (
                  <div className="text-xs text-muted-foreground">
                    Verified by {requirementProgress.verifiedBy} on{' '}
                    {new Date(requirementProgress.verifiedAt as string).toLocaleDateString()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Prerequisites section */}
        {criteria.prerequisites && criteria.prerequisites.length > 0 && (
          <div className="mt-8">
            <h3 className="font-medium mb-4">Prerequisites</h3>
            <div className="space-y-2">
              {criteria.prerequisites.map((prerequisite, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-muted rounded-lg">
                  <div>
                    <span className="font-medium">{prerequisite.programTitle}</span>
                    {prerequisite.programCategory && (
                      <span className="text-sm text-muted-foreground ml-2">
                        ({prerequisite.programCategory})
                      </span>
                    )}
                  </div>
                  <Badge variant={progress.prerequisites.met ? "default" : "outline"}>
                    {progress.prerequisites.met ? "Met" : "Not Met"}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Certification thresholds */}
        <div className="mt-8">
          <h3 className="font-medium mb-4">Certification Thresholds</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-3 bg-muted rounded-lg text-center">
              <div className="text-sm text-muted-foreground">Pass</div>
              <div className="font-medium">{criteria.passingThreshold}%</div>
            </div>
            <div className="p-3 bg-muted rounded-lg text-center">
              <div className="text-sm text-muted-foreground">Merit</div>
              <div className="font-medium">{criteria.meritThreshold}%</div>
            </div>
            <div className="p-3 bg-muted rounded-lg text-center">
              <div className="text-sm text-muted-foreground">Distinction</div>
              <div className="font-medium">{criteria.distinctionThreshold}%</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CertificationProgress;
